/**
 * W9 — Publish / handoff. The plan-of-record seam: run `publish-preview` (GET)
 * to validate the draft + diff it against the last published version, then
 * `publish` (POST) which (a) marks the Oxbo-Z baseline for plan-vs-actual and
 * (b) emits the Aura `secuencia_operativa` payload — replacing the incumbent
 * AFE-Design feed.
 *
 * The publish button mirrors the backend gate exactly (publishModel.resolveGate):
 * enabled only when the plan is approved/locked AND validation has no blocking
 * issue. A draft can never publish, even when every check passes.
 *
 * Wired to pApi getPublishPreview + publishPlan (MSW-seeded). Built to
 * w9-publish-handoff.html on the shared kit.
 */
import { useMemo } from "react";
import { useAppSelector } from "@app/hooks";
import { useLocale } from "@hooks/useLocale";
import { useGetPlanQuery, useGetPublishPreviewQuery, usePublishPlanMutation } from "@api/pApi";
import type { PublishDiffRow } from "@api/types";
import { QueryState } from "@components/ui/QueryState";
import { enrichEventCode, EVENT_LETTER_OP_VAR } from "@lib/eventEnrichment";
import { asArray } from "@lib/asArray";
import {
  checkRows,
  checkCounts,
  extraIssues,
  resolveGate,
  type CheckCode,
  type CheckRow,
} from "./publishModel";
import "./publish.css";

/** Fallback plan the screen opens against when W0 hasn't set an active plan. */
const DEFAULT_PLAN_ID = "pl-fp1216-pnc";

/** i18n key for each named check's title. */
const CHECK_TITLE: Record<CheckCode, string> = {
  required_fields: "publish.check.requiredFields",
  tvd_le_md: "publish.check.tvdLeMd",
  monotonic_depth: "publish.check.monotonic",
  cost_balance: "publish.check.costBalance",
  casing_nesting: "publish.check.casingNesting",
  program_sequence: "publish.check.programSeq",
};
/** i18n key for the passing (good) description of each check. */
const CHECK_OK_DESC: Record<CheckCode, string> = {
  required_fields: "publish.check.requiredFields.ok",
  tvd_le_md: "publish.check.tvdLeMd.ok",
  monotonic_depth: "publish.check.monotonic.ok",
  cost_balance: "publish.check.costBalance.ok",
  casing_nesting: "publish.check.casingNesting.ok",
  program_sequence: "publish.check.programSeq.ok",
};
/** The screen a failing check links out to, so the author can fix it. */
const CHECK_FIX_ROUTE: Partial<Record<CheckCode, string>> = {
  required_fields: "/workspace",
  tvd_le_md: "/hole-plan",
  monotonic_depth: "/program",
  cost_balance: "/cost",
  casing_nesting: "/casing",
  program_sequence: "/program",
};

export function Publish() {
  const { t } = useLocale();
  const activePlanId = useAppSelector((s) => s.wellContext.activePlanId);
  const planId = activePlanId ?? DEFAULT_PLAN_ID;

  const planQ = useGetPlanQuery(planId);
  const previewQ = useGetPublishPreviewQuery(planId);
  const [publish, publishState] = usePublishPlanMutation();

  const plan = planQ.data;
  const preview = previewQ.data;

  // asArray at the ingestion boundary: a real backend can return a
  // present-but-non-array `issues` / `diff` (`{}`, `{issues:null}`, an error
  // envelope). checkRows/checkCounts/asArray coerce so the checklist + diff
  // render a safe empty state instead of throwing (the Oxbo-Z crash class).
  const rows = useMemo<CheckRow[]>(() => checkRows(preview?.issues), [preview?.issues]);
  const counts = useMemo(() => checkCounts(preview?.issues), [preview?.issues]);
  const extras = useMemo(() => extraIssues(preview?.issues), [preview?.issues]);
  const diff = useMemo<PublishDiffRow[]>(() => asArray<PublishDiffRow>(preview?.diff), [preview?.diff]);

  const gate = useMemo(() => resolveGate(preview, plan?.status), [preview, plan?.status]);

  const enr = enrichEventCode(plan?.event_code);
  const opVar = enr.letter ? EVENT_LETTER_OP_VAR[enr.letter] : "--op-unconv";

  const published = publishState.data;

  async function onPublish() {
    if (!gate.canPublish) return;
    await publish(planId).unwrap();
  }

  // Why the gate is closed, in priority order (blocking check first).
  const gateReason: string = !gate.validationReady
    ? counts.error > 0
      ? t("publish.gate.blocked", { n: counts.error })
      : t("publish.gate.notReady")
    : !gate.statusReady
      ? t("publish.gate.notApproved", {
          status: plan ? t(`plan.status.${plan.status}` as never, { n: plan.version }) : "—",
        })
      : t("publish.gate.ready");

  return (
    <div>
      <div className="screen-head">
        <h1>{t("publish.title")}</h1>
        <span className="badge info">{t("publish.badge")}</span>
      </div>

      {/* event / plan context */}
      {plan && (
        <div className="chip-ctx" style={{ marginBottom: 16, flexWrap: "wrap" }}>
          <span className="k">{t("publish.ctx.well")}</span>
          <span className="mono">{plan.well_uwi}</span>
          <span className="dim">·</span>
          <span className="k">{t("publish.ctx.event")}</span>
          <span className="letter-badge" style={{ background: `var(${opVar})` }}>
            <span className="sw" style={{ background: "var(--head)" }} />
            {enr.letter ?? "?"} · {plan.event_code}
          </span>
          {plan.afe_no && (
            <>
              <span className="dim">·</span>
              <span className="k">{t("publish.ctx.afe")}</span>
              <span className="mono">{plan.afe_no}</span>
            </>
          )}
          <span className="verchip">v{plan.version}</span>
        </div>
      )}

      <div className="section-note">
        <span className="qm">i</span>
        <span>{t("publish.note")}</span>
      </div>

      <QueryState
        isLoading={previewQ.isLoading || planQ.isLoading}
        isError={previewQ.isError}
        onRetry={() => void previewQ.refetch()}
      >
        <div className="pub-grid">
          <div>
            {/* ── Validation checklist ─────────────────────────────── */}
            <section className="card" style={{ padding: 16, marginBottom: 18 }}>
              <div className="rowflex" style={{ marginBottom: 4 }}>
                <h2 style={{ margin: 0 }}>{t("publish.checklist.title")}</h2>
              </div>
              <div className="chk-summary" role="status" aria-live="polite">
                <span className="s good">
                  <span className="n tab-nums">{counts.ok}</span> {t("publish.count.ok")}
                </span>
                <span className="s warn">
                  <span className="n tab-nums">{counts.warn}</span> {t("publish.count.warn")}
                </span>
                <span className="s crit">
                  <span className="n tab-nums">{counts.error}</span> {t("publish.count.blocking")}
                </span>
              </div>

              <div className="checklist">
                {rows.map((r) => (
                  <CheckItem key={r.code} row={r} />
                ))}
                {/* Any issue whose code is not a named check still shows here. */}
                {extras.map((iss, i) => (
                  <div
                    key={`x-${i}`}
                    className={`chk ${iss.level === "error" ? "crit" : "warn"}`}
                    data-testid="extra-issue"
                  >
                    <span className="dot" aria-hidden="true">
                      {iss.level === "error" ? <IcAlert /> : <IcWarn />}
                    </span>
                    <div className="body">
                      <b>{iss.code}</b>
                      <p>{iss.message}</p>
                    </div>
                    <div className="side">
                      <span className={`sev-badge ${iss.level === "error" ? "crit" : "warn"}`}>
                        {iss.level === "error" ? t("publish.sev.blocking") : t("publish.sev.warn")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* ── Diff vs previously published version ───────────────── */}
            <section className="card" style={{ padding: 16 }}>
              <div className="rowflex" style={{ marginBottom: 2 }}>
                <h2 style={{ margin: 0 }}>{t("publish.diff.title")}</h2>
              </div>
              <p className="dim" style={{ fontSize: 12.5, margin: "2px 0 12px" }}>
                {preview?.prior_version != null
                  ? t("publish.diff.sub", { prior: preview.prior_version, next: plan?.version ?? "—" })
                  : t("publish.diff.first")}
              </p>

              {diff.length === 0 ? (
                <div className="empty-state">{t("publish.diff.none")}</div>
              ) : (
                <div className="difflist">
                  {diff.map((d, i) => (
                    <div className="diffrow" key={`${d.dataset}-${d.field}-${i}`}>
                      <span className="lbl">
                        <span className="dtchip">{d.dataset}</span> {d.field}
                      </span>
                      <span className="diffval tab-nums">
                        <span className="was">{d.before ?? t("common.na")}</span>
                        <span className="arr" aria-hidden="true">
                          →
                        </span>
                        <b>{d.after ?? t("common.na")}</b>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* ── Sticky publish rail ─────────────────────────────────── */}
          <aside className="pubrail">
            <div className="card">
              <h2 style={{ fontSize: 14, margin: "0 0 10px" }}>{t("publish.rail.title")}</h2>

              {published ? (
                <div className="gate-note ok" role="status">
                  <IcCheck />
                  <span>
                    {t("publish.done", { version: published.version })}{" "}
                    {published.z_baseline ? t("publish.done.z") : ""}{" "}
                    {published.aura_emitted
                      ? t("publish.done.aura", { n: asArray(published.aura_data_types).length })
                      : ""}
                  </span>
                </div>
              ) : (
                <div className={`gate-note ${gate.canPublish ? "ok" : ""}`}>
                  {gate.canPublish ? <IcCheck /> : <IcAlert />}
                  <span>{gateReason}</span>
                </div>
              )}

              <button
                className="btn primary"
                type="button"
                id="publishBtn"
                onClick={() => void onPublish()}
                disabled={!gate.canPublish || publishState.isLoading || Boolean(published)}
                style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
              >
                <span className="ic ic-share" aria-hidden="true" />
                {publishState.isLoading
                  ? t("publish.publishing")
                  : published
                    ? t("publish.published")
                    : t("publish.action")}
              </button>

              {publishState.isError && (
                <p className="dim" style={{ fontSize: 11.5, margin: "8px 0 0", color: "var(--crit)" }}>
                  {t("publish.error")}
                </p>
              )}
              <p className="dim" style={{ fontSize: 11.5, margin: "10px 0 0", lineHeight: 1.5 }}>
                {t("publish.rail.hint")}
              </p>
            </div>

            {/* Publish targets */}
            <div className="card">
              <h2 style={{ fontSize: 14, margin: "0 0 10px" }}>{t("publish.targets.title")}</h2>
              <div className="tgt">
                <span className="ic ic-daily" style={{ color: "var(--oxbo-z)" }} aria-hidden="true" />
                <div>
                  <b>{t("publish.target.z")}</b>
                  <p className="dim">{t("publish.target.z.sub")}</p>
                </div>
              </div>
              <div className={`tgt ${gate.canPublish || published ? "" : "blocked"}`}>
                <span className="ic ic-share" style={{ color: "var(--oxbo-u)" }} aria-hidden="true" />
                <div>
                  <b>{t("publish.target.aura")}</b>
                  <p className="dim">{t("publish.target.aura.sub")}</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </QueryState>
    </div>
  );
}

/** One named-check row of the validation checklist. */
function CheckItem({ row }: { row: CheckRow }) {
  const { t } = useLocale();
  const cls = row.level === "error" ? "crit" : row.level === "warn" ? "warn" : "good";
  const fixRoute = CHECK_FIX_ROUTE[row.code];
  return (
    <div className={`chk ${cls}`} data-check={row.code}>
      <span className="dot" aria-hidden="true">
        {row.level === "good" ? <IcCheck /> : row.level === "warn" ? <IcWarn /> : <IcAlert />}
      </span>
      <div className="body">
        <b>{t(CHECK_TITLE[row.code] as never)}</b>
        <p>{row.level === "good" ? t(CHECK_OK_DESC[row.code] as never) : row.message}</p>
      </div>
      <div className="side">
        <span className={`sev-badge ${cls}`}>
          {row.level === "good"
            ? t("publish.sev.ok")
            : row.level === "warn"
              ? t("publish.sev.warn")
              : t("publish.sev.blocking")}
        </span>
        {row.level !== "good" && fixRoute && (
          <a className="fixlink" href={fixRoute}>
            {t("publish.fix")}
          </a>
        )}
      </div>
    </div>
  );
}

function IcCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  );
}
function IcWarn() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 8.5v5M12 17h.01" />
      <path d="M10.3 3.9 2.6 18a1.6 1.6 0 0 0 1.4 2.4h16a1.6 1.6 0 0 0 1.4-2.4L13.7 3.9a1.6 1.6 0 0 0-2.8 0Z" />
    </svg>
  );
}
function IcAlert() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 8.5v5M12 17h.01" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}
